import useOptimisticCreateCustomerContactOnCustomer from '@/api/mutations/customers/optimistic/useOptimisticCreateCustomerContactOnCustomer';
import useOptimisticDeleteCustomerContactOnCustomer from '@/api/mutations/customers/optimistic/useOptimisticDeleteCustomerContactOnCustomer';
import useOptimisticSetCustomerPrimaryContact from '@/api/mutations/customers/optimistic/useOptimisticSetCustomerPrimaryContact';
import useOptimisticUpdateCustomerContactOnCustomer from '@/api/mutations/customers/optimistic/useOptimisticUpdateCustomerContactOnCustomer';
import { CustomerContact } from '@/gql/graphql';
import AddEditContactModal, {
  IProps as IAddEditContactModalProps,
} from '@/ui/components/addEditContactControl/AddEditContactModal';
import {
  AnchorButton,
  Button,
  ManageableItemsList,
  Modal,
  TTableRowAction,
} from '@payaca/ambrosia';

import UntitledIcon from '@payaca/untitled-icons';
import { FC, useCallback, useMemo, useState } from 'react';
import { Link, useHistory, useParams, useRouteMatch } from 'react-router-dom';
import useGetCustomer from '../../../../api/queries/customers/useGetCustomer';

type TableData = CustomerContact;

const CustomerContactsTable: FC = () => {
  const { customerId } = useParams<{ customerId: string }>();

  /**
   * state
   */
  const [searchQuery, setSearchQuery] = useState('');

  /**
   * React router
   */
  const history = useHistory();
  const { path, url } = useRouteMatch();
  const isAddingContact = useRouteMatch({
    path: `${path}/add`,
    exact: true,
  });
  const isEditingContact = useRouteMatch<{
    contactId: string;
  }>({
    path: `${path}/:contactId/edit`,
    exact: true,
  });
  const isDeletingContact = useRouteMatch<{
    contactId: string;
  }>({
    path: `${path}/:contactId/delete`,
    exact: true,
  });

  /**
   * Queries
   */
  const { customer } = useGetCustomer(customerId);

  /**
   * Mutations
   */
  const { mutate: createCustomerContact } =
    useOptimisticCreateCustomerContactOnCustomer(customerId);
  const { mutate: updateCustomerContact } =
    useOptimisticUpdateCustomerContactOnCustomer(customerId);
  const { mutate: deleteCustomerContact } =
    useOptimisticDeleteCustomerContactOnCustomer(customerId);
  const { mutate: setPrimaryContact } =
    useOptimisticSetCustomerPrimaryContact(customerId);

  const rowActions = useMemo<(row: TableData) => TTableRowAction<TableData>[]>(
    () => (row) => {
      const actions: TTableRowAction<TableData>[] = [
        {
          label: 'Edit',
          onClick: (contact) => history.push(`${url}/${contact.id}/edit`),
        },
        {
          label: 'Delete',
          onClick: (contact) => history.push(`${url}/${contact.id}/delete`),
        },
      ];

      if (!row.isPrimary) {
        actions.splice(1, 0, {
          label: 'Make primary Contact',
          onClick: () => setPrimaryContact({ contactId: row.id, customerId }),
        });
      }

      return actions;
    },
    [history, url, setPrimaryContact, customerId]
  );

  const handleAddEditCustomerContact = useCallback<
    Required<IAddEditContactModalProps>['onSave']
  >(
    (contact) => {
      const { id, ...rest } = contact;

      if (id) {
        updateCustomerContact({
          customerContactId: id,
          ...rest,
        });
      } else {
        createCustomerContact({ customerId, ...rest });
      }

      history.push(url);
    },
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useCallback has missing dependencies: 'createCustomerContact', and 'customerId'
    [updateCustomerContact, history, url]
  );

  const handleDeleteCustomerContact = useCallback(() => {
    if (!isDeletingContact) {
      // should never get here
      return;
    }

    deleteCustomerContact({
      customerContactId: isDeletingContact?.params.contactId,
    });

    history.push(url);
  }, [isDeletingContact, history, url, deleteCustomerContact]);

  const contacts = customer?.contacts || [];
  const filteredContacts = searchQuery
    ? contacts.filter((contact) =>
        String(contact['name'])
          .toLowerCase()
          .includes(searchQuery.toLowerCase())
      )
    : contacts;

  return (
    <div className="@container">
      <ManageableItemsList>
        <ManageableItemsList.HeaderBar
          heading="Customer Contacts"
          buttons={
            <AnchorButton as={Link} to={`${url}/add`} size={'sm'}>
              Add Contact
            </AnchorButton>
          }
        />

        <ManageableItemsList.ActionBar>
          <ManageableItemsList.ActionBar.SearchInput
            value={searchQuery}
            onChange={setSearchQuery}
          />
        </ManageableItemsList.ActionBar>

        <ManageableItemsList.Table
          uniqueKey="id"
          items={filteredContacts}
          itemActions={rowActions}
        >
          <ManageableItemsList.Table.Column header="Name" field="name" />
          <ManageableItemsList.Table.Column<TableData, 'email'>
            header="Email"
            field="email"
            render={(field) => {
              return <a href={`mailTo:${field}`}>{field}</a>;
            }}
          />
          <ManageableItemsList.Table.Column<TableData, 'phone'>
            header="Telephone"
            field="phone"
            render={(field) => {
              return <a href={`tel:${field}`}>{field}</a>;
            }}
          />
          <ManageableItemsList.Table.Column
            className="max-w-[400px] truncate"
            header="Description"
            field="description"
          />
          <ManageableItemsList.Table.Column<TableData, 'isPrimary'>
            field="isPrimary"
            header="Primary Contact"
            render={(isPrimary) => {
              if (isPrimary) {
                return <UntitledIcon className="h-6 w-6" name="check" />;
              }
            }}
          />
        </ManageableItemsList.Table>
      </ManageableItemsList>

      <AddEditContactModal
        title="Add Customer Contact"
        isOpen={!!isAddingContact}
        onClose={() => history.push(url)}
        onSave={handleAddEditCustomerContact}
      />

      <AddEditContactModal
        title="Edit Customer Contact"
        isOpen={!!isEditingContact}
        onClose={() => history.push(url)}
        contact={customer?.contacts.find(
          (contact) => contact.id === isEditingContact?.params.contactId
        )}
        onSave={handleAddEditCustomerContact}
      />

      <Modal
        title="Delete Customer Contact"
        isOpen={!!isDeletingContact}
        onClose={() => history.push(url)}
      >
        <Modal.Body>
          <p>Are you sure you want to delete this Contact?</p>
        </Modal.Body>
        <Modal.Footer>
          <Modal.Footer.Actions>
            <Button variant={'outline'} onClick={() => history.push(url)}>
              Cancel
            </Button>
            <Button colour={'red'} onClick={handleDeleteCustomerContact}>
              Delete
            </Button>
          </Modal.Footer.Actions>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CustomerContactsTable;
